import { z } from "zod";
import { opaqueIdSchema, sha256Schema } from "./opaque.js";
const uniqueIds = z.array(opaqueIdSchema).superRefine((values, context) => {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", message: "Identifiers must be unique." });
});
const uniquePassageKeys = z
    .array(z.string().trim().min(1).max(200))
    .superRefine((values, context) => {
    if (new Set(values).size !== values.length)
        context.addIssue({ code: "custom", message: "Passage keys must be unique." });
});
export const narrativeRawMemoryVisibilitySchema = z.enum([
    "BOOK_ELIGIBLE",
    "EDITOR_ONLY",
    "PRIVATE",
    "REVEAL_PENDING",
    "EXCLUDED",
]);
export const narrativeRawMemorySegmentSchema = z
    .object({
    segmentId: opaqueIdSchema,
    sourceId: opaqueIdSchema,
    sourceRevisionId: opaqueIdSchema,
    speakerPersonIds: uniqueIds.min(1).max(3),
    start: z.number().int().nonnegative(),
    end: z.number().int().positive(),
    exactText: z.string().min(1).max(12_000),
    checksumSha256: sha256Schema,
    supportedClaimIds: uniqueIds.max(200),
    kind: z.enum(["SCENE", "FACT", "VOICE", "REFLECTION", "ARTIFACT", "CORRECTION", "WITNESS"]),
    topics: z.array(z.string().trim().min(1).max(120)).min(1).max(20),
    visibility: narrativeRawMemoryVisibilitySchema,
    synthetic: z.literal(true),
})
    .strict()
    .superRefine((segment, context) => {
    if (segment.start >= segment.end || segment.end - segment.start !== segment.exactText.length)
        context.addIssue({
            code: "custom",
            path: ["end"],
            message: "Raw-memory offsets must bind the complete exact text.",
        });
    if (segment.visibility !== "BOOK_ELIGIBLE" && segment.supportedClaimIds.length > 0)
        context.addIssue({
            code: "custom",
            path: ["supportedClaimIds"],
            message: "Restricted raw memory cannot support a book claim.",
        });
});
export const narrativeRawMemoryPacketSchema = z
    .object({
    contract: z.literal("narrative-raw-memory/1"),
    product: z.enum(["MY_STORY", "OUR_STORY"]),
    bookId: opaqueIdSchema,
    sourcePacketChecksumSha256: sha256Schema,
    segments: z.array(narrativeRawMemorySegmentSchema).min(5).max(500),
    createdAt: z.string().datetime(),
})
    .strict();
export const narrativeVoiceProfileSchema = z
    .object({
    speakerPersonId: opaqueIdSchema,
    anchorSegmentIds: uniqueIds.min(1).max(40),
    qualities: z.array(z.string().trim().min(1).max(100)).min(2).max(20),
    characteristicLanguage: z.array(z.string().trim().min(1).max(200)).min(1).max(30),
    sentencePatterns: z.array(z.string().trim().min(1).max(240)).min(1).max(20),
    avoid: z.array(z.string().trim().min(1).max(240)).min(1).max(30),
})
    .strict();
export const narrativeSentenceBindingSchema = z
    .object({
    sentence: z.string().trim().min(1).max(4_000),
    claimIds: uniqueIds.min(1).max(50),
    sourceSegmentIds: uniqueIds.min(1).max(50),
})
    .strict();
export const narrativeEditorialPassageSchema = z
    .object({
    passageId: opaqueIdSchema,
    chapterId: opaqueIdSchema,
    purpose: z.string().trim().min(1).max(1_000),
    form: z.enum([
        "FIRST_PERSON_MEMOIR",
        "MAYA_VOICE",
        "ROWAN_VOICE",
        "SHARED_WE",
        "SIDE_BY_SIDE",
        "JOINT_REFLECTION",
        "WITNESS_CONTEXT",
    ]),
    speakerPersonIds: uniqueIds.min(1).max(3),
    claimIds: uniqueIds.min(1).max(200),
    sourceSegmentIds: uniqueIds.min(1).max(100),
    candidate: z.string().min(1).max(20_000),
    critique: z.array(z.string().trim().min(1).max(1_000)).min(1).max(10),
    revision: z.string().min(1).max(20_000),
    sentenceBindings: z.array(narrativeSentenceBindingSchema).min(1).max(200),
    requiredApproverPersonIds: uniqueIds.max(3),
    approvedByPersonIds: uniqueIds.max(3),
    repairCount: z.number().int().min(0).max(2),
})
    .strict()
    .superRefine((passage, context) => {
    const approved = new Set(passage.approvedByPersonIds);
    if (passage.requiredApproverPersonIds.some((id) => !approved.has(id)))
        context.addIssue({
            code: "custom",
            path: ["approvedByPersonIds"],
            message: "Every required fixture approver must approve the accepted revision.",
        });
    const claims = new Set(passage.claimIds);
    const segments = new Set(passage.sourceSegmentIds);
    passage.sentenceBindings.forEach((binding, index) => {
        if (binding.claimIds.some((id) => !claims.has(id)))
            context.addIssue({
                code: "custom",
                path: ["sentenceBindings", index, "claimIds"],
                message: "Sentence bindings may use only passage-approved claims.",
            });
        if (binding.sourceSegmentIds.some((id) => !segments.has(id)))
            context.addIssue({
                code: "custom",
                path: ["sentenceBindings", index, "sourceSegmentIds"],
                message: "Sentence bindings may use only passage-approved raw segments.",
            });
    });
});
export const myStoryScenePlanSchema = z
    .object({
    sceneId: opaqueIdSchema,
    chapterId: opaqueIdSchema,
    title: z.string().trim().min(1).max(200),
    purpose: z.string().trim().min(1).max(1_000),
    claimIds: uniqueIds.min(1).max(200),
    sourceSegmentIds: uniqueIds.min(1).max(100),
    eventIds: uniqueIds.max(50),
    contradictionIds: uniqueIds.max(20),
    temporalLabel: z.string().trim().min(1).max(160),
    chronologyConfidence: z.enum(["EXACT", "APPROXIMATE", "DISPUTED", "UNKNOWN"]),
    role: z.enum(["ORIGIN", "DEPARTURE", "REINVENTION", "CARE", "ORDINARY_LIFE", "LEGACY"]),
    ordinaryLife: z.boolean(),
})
    .strict();
export const myStoryEditorialPlanSchema = z
    .object({
    contract: z.literal("my-story-editorial-plan/1"),
    bookId: opaqueIdSchema,
    fixturePrompt: z
        .object({
        id: z.literal("my-story-editorial-fixture"),
        version: z.literal("1.0.0"),
        hashSha256: sha256Schema,
    })
        .strict(),
    storyPromise: z.string().trim().min(1).max(1_000),
    voiceProfile: narrativeVoiceProfileSchema,
    thematicThreads: z.array(z.string().trim().min(1).max(200)).min(2).max(12),
    scenes: z.array(myStoryScenePlanSchema).min(5).max(100),
    passages: z.array(narrativeEditorialPassageSchema).min(5).max(250),
})
    .strict();
export const ourStoryVoiceBeatSchema = z
    .object({
    beatId: opaqueIdSchema,
    chapterId: opaqueIdSchema,
    purpose: z.string().trim().min(1).max(1_000),
    form: z.enum([
        "MAYA_VOICE",
        "ROWAN_VOICE",
        "SHARED_WE",
        "SIDE_BY_SIDE",
        "JOINT_REFLECTION",
        "WITNESS_CONTEXT",
    ]),
    speakerPersonIds: uniqueIds.min(1).max(3),
    claimIds: uniqueIds.min(1).max(200),
    sourceSegmentIds: uniqueIds.min(1).max(100),
    differenceIds: uniqueIds.max(30),
    sharedEvidenceEligible: z.boolean(),
    requiredApproverPersonIds: uniqueIds.max(3),
    narrativeAgencyWeight: z.number().min(0).max(10),
})
    .strict();
export const ourStoryEditorialPlanSchema = z
    .object({
    contract: z.literal("our-story-editorial-plan/1"),
    bookId: opaqueIdSchema,
    fixturePrompt: z
        .object({
        id: z.literal("our-story-editorial-fixture"),
        version: z.literal("1.0.0"),
        hashSha256: sha256Schema,
    })
        .strict(),
    storyPromise: z.string().trim().min(1).max(1_000),
    voiceProfiles: z.array(narrativeVoiceProfileSchema).length(2),
    beats: z.array(ourStoryVoiceBeatSchema).min(8).max(250),
    passages: z.array(narrativeEditorialPassageSchema).min(5).max(250),
    balance: z
        .object({
        principalAgency: z.record(opaqueIdSchema, z.number().min(0)),
        maximumAgencyDelta: z.number().min(0).max(10),
        actualAgencyDelta: z.number().min(0).max(10),
    })
        .strict(),
})
    .strict();
export const narrativeDesignSystemManifestSchema = z
    .object({
    contract: z.literal("narrative-design-system/1"),
    product: z.enum(["MY_STORY", "OUR_STORY"]),
    version: z.string().trim().min(1).max(100),
    direction: z.string().trim().min(1).max(1_000),
    typeCatalog: z.array(z
        .object({
        role: z.string().trim().min(1).max(80),
        family: z.string().trim().min(1).max(120),
        weight: z.number().int().min(100).max(900),
        minimumPoints: z.number().positive().max(100),
    })
        .strict()),
    componentCatalog: z.array(z.string().trim().min(1).max(120)).min(8).max(100),
    pageFamilies: z.array(z.string().trim().min(1).max(120)).min(6).max(50),
    pagePlans: z.array(z
        .object({
        chapterId: opaqueIdSchema,
        family: z.string().trim().min(1).max(120),
        rationale: z.string().trim().min(1).max(500),
        contentChecksumSha256: sha256Schema,
    })
        .strict()),
    preflight: z
        .object({
        passed: z.boolean(),
        manuscriptChecksumSha256: sha256Schema,
        renderedTextChecksumSha256: sha256Schema,
        omittedPassageIds: uniquePassageKeys,
        duplicatePassageIds: uniquePassageKeys,
        minimumBodyPoints: z.number().positive(),
        measuredBodyPoints: z.number().positive(),
        findings: z.array(z.string().trim().min(1).max(500)).max(100),
    })
        .strict(),
})
    .strict();
export const narrativeEditorialQualityReportSchema = z
    .object({
    contract: z.literal("narrative-editorial-quality/1"),
    product: z.enum(["MY_STORY", "OUR_STORY"]),
    passed: z.boolean(),
    checks: z.array(z
        .object({
        id: z.string().trim().min(1).max(120),
        passed: z.boolean(),
        score: z.number().min(0).max(100),
        detail: z.string().trim().min(1).max(1_000),
    })
        .strict()),
    fixtureLimitations: z.array(z.string().trim().min(1).max(1_000)).min(1),
})
    .strict();
//# sourceMappingURL=narrative-editorial.js.map